import React, { useState, useEffect } from 'react';
import { FaTimes, FaChartBar, FaChartLine, FaChartPie, FaChartArea, FaTable } from 'react-icons/fa';
import BarChart from './BarChart';
import ScatterChart from './ScatterChart';
import DonutChart from './DonutChart';
import jwt_decode from 'jwt-decode';
import './ReportChart.css';

// Import Chart.js components for multiseries support
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Scatter } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AreaChart } from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ReportChart = ({
  isOpen,
  onClose,
  reportData,
  onSaveChart,
  chartValues = [],
  chartLabels = [],
  xAxisLabel = '',
  yAxisLabel = '',
  chartTitle = '',
  summarizedData = {}, // Enhanced summarized data with field details
  summarizedFields = {}, // Field definitions for summarized data
  previewData = [], // Preview data for record count calculation
  selectedGroups = [], // Selected groups for record count calculation
  editingChart = null // Chart being edited
}) => {
  const [chartConfig, setChartConfig] = useState({
    title: '',
    chartType: 'bar',
    selectedCategoryField: '', // Field for X-axis (categories)
    selectedValueFields: [],   // Multiple fields for Y-axis (values) - changed to array
    xAxisLabel: '',            // Custom X-axis label
    yAxisLabel: '',            // Custom Y-axis label
    fieldColors: {},           // Custom colors for each field
    showTooltips: true,        // Show tooltips on hover
    showLabels: false,         // Show data labels on chart elements
    labelColor: '#000000',     // Color for data labels
    scatterXAxisLabel: '',     // Custom X-axis label for scatter charts
    scatterYAxisLabel: '',     // Custom Y-axis label for scatter charts
    sortOrder: 1,              // Sort order for chart display
    columnSpan: 1,             // Column span: 1 = half width, 2 = full width
    // Donut chart specific properties
    pieValueField: '',         // Value field for donut chart (summarized field)
    pieSliceField: '',         // Field to slice by (grouped field)
    showValues: false,         // Show values on donut chart segments
    showPercentages: false,    // Show percentages on donut chart segments
    showTotal: true,           // Show total in center of doughnut chart
    legendColors: {},          // Custom colors for legend items {label: color}
    legendPosition: 'bottom'   // Legend position
  });

  const [isLoading, setIsLoading] = useState(false);

  // Chart types available
  const chartTypes = [
    { value: 'bar', label: 'Column Chart', icon: FaChartBar },
    { value: 'horizontalBar', label: 'Bar Chart', icon: FaChartBar },
    { value: 'line', label: 'Line Chart', icon: FaChartLine },
    { value: 'scatter', label: 'Scatter Chart', icon: FaChartLine },
    { value: 'donut', label: 'Donut Chart', icon: FaChartPie },
    { value: 'area', label: 'Area Chart', icon: FaChartArea },
  ];

  // Handle input changes
  const handleInputChange = (field, value) => {
    setChartConfig(prev => {
      const newConfig = { ...prev, [field]: value };

      // Reset chart-specific fields when chart type changes
      if (field === 'chartType') {
        if (value === 'donut') {
          // Reset to donut chart defaults
          newConfig.pieValueField = '';
          newConfig.pieSliceField = '';
          newConfig.showValues = false;
          newConfig.showPercentages = false;
          newConfig.showTotal = true;
          newConfig.legendColors = {};
          newConfig.valueFieldType = 'number';
          // Clear regular chart fields
          newConfig.selectedCategoryField = '';
          newConfig.selectedValueFields = [];
          newConfig.xAxisLabel = '';
          newConfig.yAxisLabel = '';
          newConfig.fieldColors = {};
        } else if (value === 'scatter') {
          // Reset to scatter chart defaults
          newConfig.scatterXAxis = '';
          newConfig.scatterYAxis = '';
          newConfig.scatterGroupField = '';
          newConfig.scatterXAxisLabel = '';
          newConfig.scatterYAxisLabel = '';
          // Clear donut chart fields
          newConfig.pieValueField = '';
          newConfig.pieSliceField = '';
          newConfig.showValues = false;
          newConfig.showPercentages = false;
          newConfig.showTotal = true;
          newConfig.legendColors = {};
        } else {
          // Reset to regular chart defaults
          newConfig.selectedCategoryField = '';
          newConfig.selectedValueFields = [];
          newConfig.xAxisLabel = '';
          newConfig.yAxisLabel = '';
          newConfig.fieldColors = {};
          // Clear donut chart fields
          newConfig.pieValueField = '';
          newConfig.pieSliceField = '';
          newConfig.showValues = false;
          newConfig.showPercentages = false;
          newConfig.showTotal = true;
          newConfig.legendColors = {};
          // Clear scatter chart fields
          newConfig.scatterXAxis = '';
          newConfig.scatterYAxis = '';
          newConfig.scatterGroupField = '';
          newConfig.scatterXAxisLabel = '';
          newConfig.scatterYAxisLabel = '';
        }
      }

      return newConfig;
    });
  };

  // Convert rgba to hex for color picker
  const rgbaToHex = (rgba) => {
    const values = rgba.match(/\d+/g);
    if (!values || values.length < 3) return '#000000';

    const r = parseInt(values[0]);
    const g = parseInt(values[1]);
    const b = parseInt(values[2]);

    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Get default color for a label based on its index
  const getDefaultColorForLabel = (labelIndex) => {
    const baseColors = [
      'rgba(54, 162, 235, 0.8)',   // Blue
      'rgba(255, 99, 132, 0.8)',   // Red
      'rgba(255, 205, 86, 0.8)',   // Yellow
      'rgba(75, 192, 192, 0.8)',   // Teal
      'rgba(153, 102, 255, 0.8)',  // Purple
      'rgba(255, 159, 64, 0.8)',   // Orange
      'rgba(199, 199, 199, 0.8)',  // Grey
      'rgba(83, 102, 255, 0.8)',   // Indigo
      'rgba(255, 99, 255, 0.8)',   // Pink
      'rgba(99, 255, 132, 0.8)',   // Green
    ];
    return baseColors[labelIndex % baseColors.length];
  };

  // Get default hex color for color picker
  const getDefaultHexColorForLabel = (labelIndex) => {
    const rgbaColor = getDefaultColorForLabel(labelIndex);
    return rgbaToHex(rgbaColor);
  };

  // Handle legend color change for donut charts
  const handleLegendColorChange = (label, color) => {
    setChartConfig(prev => {
      const newLegendColors = {
        ...prev.legendColors,
        [label]: color
      };
      return {
        ...prev,
        legendColors: newLegendColors
      };
    });

    // Force chart re-render by updating a timestamp
    setTimeout(() => {
      setChartConfig(prev => ({ ...prev, _colorUpdate: Date.now() }));
    }, 100);
  };

  // Reset legend color to default
  const resetLegendColorToDefault = (label) => {
    setChartConfig(prev => {
      const newLegendColors = { ...prev.legendColors };
      delete newLegendColors[label]; // Remove custom color to use default
      return {
        ...prev,
        legendColors: newLegendColors
      };
    });

    // Force chart re-render
    setTimeout(() => {
      setChartConfig(prev => ({ ...prev, _colorUpdate: Date.now() }));
    }, 100);
  };

  // Handle multiple value field selection
  const handleValueFieldToggle = (fieldName) => {
    setChartConfig(prev => {
      const currentFields = prev.selectedValueFields || [];
      const isSelected = currentFields.includes(fieldName);

      let newFields;
      let newFieldColors = { ...prev.fieldColors };

      if (isSelected) {
        // Remove field if already selected
        newFields = currentFields.filter(f => f !== fieldName);
        // Remove the color for this field
        delete newFieldColors[fieldName];
      } else {
        // Add field if not selected
        newFields = [...currentFields, fieldName];
        // Set default color for this field if not already set
        if (!newFieldColors[fieldName]) {
          const availableFields = getAvailableValueFields();
          const fieldIndex = availableFields.findIndex(f => f.value === fieldName);
          newFieldColors[fieldName] = getColorForField(fieldName, fieldIndex);
        }
      }

      return {
        ...prev,
        selectedValueFields: newFields,
        fieldColors: newFieldColors
      };
    });
  };

  // Generate auto labels based on selected fields
  const generateAutoLabels = () => {
    let autoXAxisLabel = 'Categories';
    let autoYAxisLabel = 'Values';

    if (summarizedData && summarizedData._metadata) {
      const metadata = summarizedData._metadata;

      // Generate X-axis label
      if (chartConfig.selectedCategoryField && metadata.group_fields[chartConfig.selectedCategoryField]) {
        const groupField = metadata.group_fields[chartConfig.selectedCategoryField];
        autoXAxisLabel = groupField.field_label || chartConfig.selectedCategoryField;
      }

      // Generate Y-axis label based on multiple selected value fields
      if (chartConfig.selectedValueFields && chartConfig.selectedValueFields.length > 0) {
        if (chartConfig.selectedValueFields.length === 1) {
          // Single field
          const fieldName = chartConfig.selectedValueFields[0];
          if (metadata.summarized_fields[fieldName]) {
            const summarizedField = metadata.summarized_fields[fieldName];
            const aggregation = summarizedField.aggregation?.toUpperCase() || 'SUM';
            autoYAxisLabel = `${summarizedField.field_label || fieldName} (${aggregation})`;
          }
        } else {
          // Multiple fields
          autoYAxisLabel = `${chartConfig.selectedValueFields.length} Metrics`;
        }
      }
    }

    return { autoXAxisLabel, autoYAxisLabel };
  };

  // Generate auto labels for scatter charts based on selected fields
  const generateScatterAutoLabels = () => {
    let autoScatterXAxisLabel = '';
    let autoScatterYAxisLabel = '';

    if (summarizedData && summarizedData._metadata) {
      const metadata = summarizedData._metadata;

      // Generate X-axis label for scatter chart
      if (chartConfig.scatterXAxis && metadata.summarized_fields[chartConfig.scatterXAxis]) {
        const xField = metadata.summarized_fields[chartConfig.scatterXAxis];
        const aggregation = xField.aggregation?.toUpperCase() || 'SUM';
        autoScatterXAxisLabel = `${xField.field_label || chartConfig.scatterXAxis} (${aggregation})`;
      }

      // Generate Y-axis label for scatter chart
      if (chartConfig.scatterYAxis && metadata.summarized_fields[chartConfig.scatterYAxis]) {
        const yField = metadata.summarized_fields[chartConfig.scatterYAxis];
        const aggregation = yField.aggregation?.toUpperCase() || 'SUM';
        autoScatterYAxisLabel = `${yField.field_label || chartConfig.scatterYAxis} (${aggregation})`;
      }
    }

    return { autoScatterXAxisLabel, autoScatterYAxisLabel };
  };

  // Generate colors for different fields
  const getColorForField = (fieldName, index) => {
    const colors = [
      '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#C9CBCF', '#8B5CF6', '#10B981', '#F59E0B',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#8B5A2B'
    ];
    return colors[index % colors.length];
  };

  // Helper function to calculate record count for a specific group
  const calculateRecordCountForGroup = (groupValue, groupField) => {
    if (!previewData || previewData.length === 0 || !groupField) {
      return 0;
    }

    // Filter records that belong to this group
    const recordsInGroup = previewData.filter(row => {
      const tableName = groupField.object_name || 'main';
      const aliasedFieldName = `${tableName}_${groupField.field_name}`;
      const fieldData = row[aliasedFieldName] || row[groupField.field_name];

      let rowGroupValue = 'N/A';
      if (fieldData) {
        if (typeof fieldData === 'object' && fieldData !== null) {
          rowGroupValue = fieldData.displayValue || fieldData.value || 'N/A';
        } else {
          rowGroupValue = fieldData;
        }
      }
      return rowGroupValue === groupValue;
    });

    return recordsInGroup.length;
  };

  // Handle color change for a specific field
  const handleFieldColorChange = (fieldName, color) => {
    setChartConfig(prev => ({
      ...prev,
      fieldColors: {
        ...prev.fieldColors,
        [fieldName]: color
      }
    }));
  };

  // Get color for a specific field (custom or default)
  const getFieldColor = (fieldName, index) => {
    if (chartConfig.fieldColors && chartConfig.fieldColors[fieldName]) {
      return chartConfig.fieldColors[fieldName];
    }
    return getColorForField(fieldName, index);
  };

  // Pre-fill form when editing an existing chart
  useEffect(() => {
    if (editingChart && isOpen) {
      console.log('Pre-filling form with editing chart:', editingChart);
      // Ensure default colors are set for any fields without custom colors (only for non-scatter charts)
      const chartType = editingChart.chartType || 'bar';
      let selectedFields = [];
      let existingColors = {};
      let defaultColors = {};

      if (chartType !== 'scatter') {
        selectedFields = editingChart.yAxisFields || (editingChart.yAxisField ? [editingChart.yAxisField] : []);
        existingColors = editingChart.fieldColors || {};

        selectedFields.forEach(fieldName => {
          if (!existingColors[fieldName]) {
            const availableFields = getAvailableValueFields();
            const fieldIndex = availableFields.findIndex(f => f.value === fieldName);
            defaultColors[fieldName] = getColorForField(fieldName, fieldIndex);
          }
        });
      }

      // Set chart configuration based on chart type

      setChartConfig({
        title: editingChart.title || '',
        chartType: chartType,
        ...(chartType === 'scatter' ? {
          // Scatter chart configuration
          selectedCategoryField: '',
          selectedValueFields: [],
          xAxisLabel: '',
          yAxisLabel: '',
          scatterXAxis: editingChart.scatterXAxis || '',
          scatterYAxis: editingChart.scatterYAxis || '',
          scatterGroupField: editingChart.scatterGroupField || '',
          scatterXAxisLabel: editingChart.scatterXAxisLabel || '',
          scatterYAxisLabel: editingChart.scatterYAxisLabel || '',
        } : {
          // Regular chart configuration
          selectedCategoryField: editingChart.xAxisField || '',
          selectedValueFields: selectedFields,
          xAxisLabel: editingChart.xAxisLabel || '',
          yAxisLabel: editingChart.yAxisLabel || '',
          scatterXAxis: '',
          scatterYAxis: '',
          scatterGroupField: '',
          scatterXAxisLabel: '',
          scatterYAxisLabel: '',
        }),
        fieldColors: { ...existingColors, ...defaultColors },
        showTooltips: editingChart.showTooltips !== undefined ? editingChart.showTooltips : true,
        showLabels: editingChart.showLabels !== undefined ? editingChart.showLabels : false,
        labelColor: editingChart.labelColor || '#000000',
        sortOrder: editingChart.sortOrder || 1,
        columnSpan: editingChart.columnSpan || 1,
        // Donut chart specific properties - map from standard fields if not already set
        pieValueField: editingChart.pieValueField || (editingChart.yAxisFields && editingChart.yAxisFields[0]) || '',
        pieSliceField: editingChart.pieSliceField || editingChart.xAxisField || '',
        showValues: editingChart.showValues !== undefined ? editingChart.showValues : false,
        showPercentages: editingChart.showPercentages !== undefined ? editingChart.showPercentages : false,
        showTotal: editingChart.showTotal !== undefined ? editingChart.showTotal : true,
        legendColors: editingChart.legendColors || {},
        legendPosition: editingChart.legendPosition || 'bottom',
      });
    } else if (isOpen && !editingChart) {
      // Reset form for new chart
      setChartConfig({
        title: '',
        chartType: 'bar',
        selectedCategoryField: '',
        selectedValueFields: [],
        xAxisLabel: '',
        yAxisLabel: '',
        fieldColors: {},
        showTooltips: true,
        showLabels: false,
        labelColor: '#000000',
        scatterXAxis: '',
        scatterYAxis: '',
        scatterGroupField: '',
        scatterXAxisLabel: '',
        scatterYAxisLabel: '',
        legendPosition: 'bottom',
        sortOrder: 1,
        columnSpan: 1,
        // Donut chart specific properties
        pieValueField: '',
        pieSliceField: '',
        showValues: false,
        showPercentages: false,
        showTotal: true,
        legendColors: {},
      });
    }
  }, [editingChart, isOpen]);

  // Update axis labels when field selections change
  useEffect(() => {
    if (isOpen && !editingChart) {
      // Only auto-update labels for new charts, not when editing
      const { autoXAxisLabel, autoYAxisLabel } = generateAutoLabels();
      const { autoScatterXAxisLabel, autoScatterYAxisLabel } = generateScatterAutoLabels();
      setChartConfig(prev => ({
        ...prev,
        xAxisLabel: prev.xAxisLabel || autoXAxisLabel,
        yAxisLabel: prev.yAxisLabel || autoYAxisLabel,
        scatterXAxisLabel: prev.scatterXAxisLabel || autoScatterXAxisLabel,
        scatterYAxisLabel: prev.scatterYAxisLabel || autoScatterYAxisLabel,
      }));
    }
  }, [chartConfig.selectedCategoryField, chartConfig.selectedValueFields, chartConfig.scatterXAxis, chartConfig.scatterYAxis, isOpen, editingChart]);

  // Ensure default colors are set for newly selected fields
  useEffect(() => {
    if (chartConfig.selectedValueFields && chartConfig.selectedValueFields.length > 0) {
      const availableFields = getAvailableValueFields();
      const updatedColors = { ...chartConfig.fieldColors };
      let colorsUpdated = false;

      chartConfig.selectedValueFields.forEach(fieldName => {
        if (!updatedColors[fieldName]) {
          const fieldIndex = availableFields.findIndex(f => f.value === fieldName);
          updatedColors[fieldName] = getColorForField(fieldName, fieldIndex);
          colorsUpdated = true;
        }
      });

      if (colorsUpdated) {
        setChartConfig(prev => ({
          ...prev,
          fieldColors: updatedColors
        }));
      }
    }
  }, [chartConfig.selectedValueFields]);

  // Get available category fields (group fields)
  const getAvailableCategoryFields = () => {
    if (!summarizedData || !summarizedData._metadata) {
      return [];
    }

    const metadata = summarizedData._metadata;
    const categoryFields = [];

    if (metadata.group_fields) {
      Object.keys(metadata.group_fields).forEach(fieldName => {
        const fieldInfo = metadata.group_fields[fieldName];
        categoryFields.push({
          value: fieldName,
          label: fieldInfo.field_label || fieldInfo.field_name,
          fieldInfo: fieldInfo
        });
      });
    }

    return categoryFields;
  };

  // Get available value fields (summarized fields)
  const getAvailableValueFields = () => {
    if (!summarizedData || !summarizedData._metadata) {
      return [];
    }

    const metadata = summarizedData._metadata;
    const valueFields = [];

    if (metadata.summarized_fields) {
      Object.keys(metadata.summarized_fields).forEach(fieldName => {
        const fieldInfo = metadata.summarized_fields[fieldName];
        valueFields.push({
          value: fieldName,
          label: `${fieldInfo.field_label || fieldInfo.field_name} (${fieldInfo.aggregation?.toUpperCase() || 'SUM'})`,
          fieldInfo: fieldInfo
        });
      });
    }

    // Add Record Count as a virtual summarized field option
    if (metadata.group_fields && Object.keys(metadata.group_fields).length > 0) {
      valueFields.push({
        value: 'record_count',
        label: 'Record Count (COUNT)',
        fieldInfo: {
          field_label: 'Record Count',
          field_name: 'record_count',
          aggregation: 'count',
          data_type: 'number',
          is_virtual: true
        }
      });
    }

    return valueFields;
  };

  // Set default field selections when summarizedData changes
  useEffect(() => {
    if (summarizedData && Object.keys(summarizedData).length > 0 && summarizedData._metadata) {
      const metadata = summarizedData._metadata;

      // Set default category field if not already selected
      if (!chartConfig.selectedCategoryField && metadata.group_fields) {
        const firstCategoryField = Object.keys(metadata.group_fields)[0];
        if (firstCategoryField) {
          setChartConfig(prev => ({ ...prev, selectedCategoryField: firstCategoryField }));
        }
      }

      // Set default value field if not already selected
      if ((!chartConfig.selectedValueFields || chartConfig.selectedValueFields.length === 0) && metadata.summarized_fields) {
        const firstValueField = Object.keys(metadata.summarized_fields)[0];
        if (firstValueField) {
          setChartConfig(prev => {
            const availableFields = getAvailableValueFields();
            const fieldIndex = availableFields.findIndex(f => f.value === firstValueField);
            const defaultColor = getColorForField(firstValueField, fieldIndex);

            return {
              ...prev,
              selectedValueFields: [firstValueField],
              fieldColors: {
                ...prev.fieldColors,
                [firstValueField]: defaultColor
              }
            };
          });
        }
      }
    }
  }, [summarizedData, chartConfig.selectedCategoryField, chartConfig.selectedValueFields]);

  // Generate chart preview data
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      try {
        const chartData = processChartData();
        // Preview data is now handled by the parent component
        console.log('Generated chart data:', chartData);
      } catch (error) {
        console.error('Error generating chart data:', error);
        // Preview data is now handled by the parent component
      } finally {
        setIsLoading(false);
      }
    }
  }, [chartConfig.chartType, chartConfig.selectedCategoryField, chartConfig.selectedValueFields, chartConfig.title, chartConfig.xAxisLabel, chartConfig.yAxisLabel, chartConfig.fieldColors, chartConfig.showTooltips, chartConfig.showLabels, chartConfig.labelColor, isOpen, chartValues, chartLabels, xAxisLabel, yAxisLabel, chartTitle, summarizedData]);

  // Extract data from enhanced summarizedData structure
  const extractDataFromSummarizedData = () => {
    if (!summarizedData || Object.keys(summarizedData).length === 0) {
      return null;
    }

    // Get the first group field and first summarized field from metadata
    const metadata = summarizedData._metadata;
    if (!metadata || !metadata.group_fields || !metadata.summarized_fields) {
      return null;
    }

    // Use selected fields if available, otherwise fall back to first available fields
    const selectedCategoryField = chartConfig.selectedCategoryField || Object.keys(metadata.group_fields)[0];
    const selectedValueFields = chartConfig.selectedValueFields && chartConfig.selectedValueFields.length > 0
      ? chartConfig.selectedValueFields
      : [Object.keys(metadata.summarized_fields)[0]].filter(Boolean);

    if (!selectedCategoryField || !selectedValueFields || selectedValueFields.length === 0) {
      console.log('Missing required fields:', { selectedCategoryField, selectedValueFields });
      return null;
    }

    console.log('Using selected fields:', { selectedCategoryField, selectedValueFields });
    console.log('Available summarized data keys:', Object.keys(summarizedData));
    console.log('Group data for', selectedCategoryField, ':', summarizedData[selectedCategoryField]);
    console.log('Metadata:', metadata);

    const groupData = summarizedData[selectedCategoryField];
    if (!groupData) {
      console.log('No group data found for field:', selectedCategoryField);
      return null;
    }

    const labels = [];
    const datasets = [];

    // Get all unique group values (categories)
    const allGroupValues = new Set();
    Object.keys(groupData).forEach(groupValue => {
      allGroupValues.add(groupValue);
    });

    labels.push(...Array.from(allGroupValues));

    // Create datasets for each selected value field
    selectedValueFields.forEach(valueFieldName => {
      let fieldInfo;
      let isVirtualRecordCount = false;

      if (valueFieldName === 'record_count') {
        // Handle virtual record count field
        fieldInfo = {
          field_label: 'Record Count',
          field_name: 'record_count',
          aggregation: 'count',
          data_type: 'number',
          is_virtual: true
        };
        isVirtualRecordCount = true;
      } else {
        fieldInfo = metadata.summarized_fields[valueFieldName];
        if (!fieldInfo) return;
      }

      const dataset = {
        label: `${fieldInfo.field_label || valueFieldName} (${fieldInfo.aggregation?.toUpperCase() || 'SUM'})`,
        data: [],
        backgroundColor: getFieldColor(valueFieldName, selectedValueFields.indexOf(valueFieldName)),
        borderColor: getFieldColor(valueFieldName, selectedValueFields.indexOf(valueFieldName)),
        borderWidth: 1
      };

      // Extract values for this field across all categories
      labels.forEach(category => {
        let parsedValue = 0;

        if (isVirtualRecordCount) {
          // For record count, calculate the actual number of records in each group
          const groupField = metadata.group_fields[selectedCategoryField];
          parsedValue = calculateRecordCountForGroup(category, groupField);
        } else {
          // Handle regular summarized fields
          const groupSummary = groupData[category];
          if (groupSummary && groupSummary[valueFieldName]) {
            const rawValue = groupSummary[valueFieldName].value;
            console.log('Raw value for', valueFieldName, 'in', category, ':', rawValue);

            if (typeof rawValue === 'number') {
              parsedValue = rawValue;
            } else if (typeof rawValue === 'string') {
              if (rawValue === 'N/A' || rawValue.trim() === '') {
                parsedValue = 0;
              } else {
                let cleanValue = rawValue.replace(/[₹$,\s]/g, '').trim();
                if (cleanValue.endsWith('%')) {
                  cleanValue = cleanValue.replace('%', '');
                }
                parsedValue = parseFloat(cleanValue) || 0;
              }
            }
          }
        }

        dataset.data.push(parsedValue);
      });

      datasets.push(dataset);
      console.log('Created dataset for field', valueFieldName, ':', dataset);
    });

    console.log('Final labels:', labels);
    console.log('Final datasets:', datasets);

    // Get field details from metadata
    const groupFieldInfo = metadata.group_fields[selectedCategoryField];

    // Use the labels from chartConfig (which will have auto-generated defaults if not customized)
    const result = {
      labels,
      datasets,
      groupFieldInfo,
      selectedValueFields,
      xAxisLabel: chartConfig.xAxisLabel || 'Categories',
      yAxisLabel: chartConfig.yAxisLabel || 'Values'
    };

    console.log('Extracted data result:', result);
    return result;
  };

  // Simple data processing using props directly
  const processChartData = () => {
    console.log('Processing chart data:', {
      chartValues,
      chartLabels,
      xAxisLabel,
      yAxisLabel,
      chartTitle,
      summarizedData: !!summarizedData && Object.keys(summarizedData).length > 0
    });

    // Try to extract data from enhanced summarizedData first
    const extractedData = extractDataFromSummarizedData();
    if (extractedData) {
      console.log('Using extracted data from summarizedData:', extractedData);

      // Ensure we have valid arrays for labels and datasets
      const labels = Array.isArray(extractedData.labels) ? extractedData.labels : [];
      const datasets = Array.isArray(extractedData.datasets) ? extractedData.datasets : [];

      console.log('Processed labels:', labels);
      console.log('Processed datasets:', datasets);

      return {
        type: chartConfig.chartType,
        labels: labels,
        data: datasets, // Use datasets directly
        datasets: datasets, // Also provide datasets for ChartDisplay
        title: chartConfig.title || chartTitle,
        xAxisTitle: extractedData.xAxisLabel,
        yAxisTitle: extractedData.yAxisLabel,
        fieldInfo: {
          groupField: extractedData.groupFieldInfo,
          selectedValueFields: extractedData.selectedValueFields
        }
      };
    }

    // No summarized data: show empty chart (no sample/dummy points)
    const hasProps =
      (Array.isArray(chartLabels) && chartLabels.length > 0) ||
      (Array.isArray(chartValues) && chartValues.length > 0);
    if (hasProps) {
      const labels = chartLabels;
      const data = chartValues;
      const fromPropsDatasets = [{
        label: 'Data',
        data,
        backgroundColor: '#36A2EB',
        borderColor: '#36A2EB',
        borderWidth: 1
      }];
      return {
        type: chartConfig.chartType,
        labels,
        data: fromPropsDatasets,
        datasets: fromPropsDatasets,
        title: chartConfig.title || chartTitle || '',
        xAxisTitle: chartConfig.xAxisLabel || xAxisLabel || 'Categories',
        yAxisTitle: chartConfig.yAxisLabel || yAxisLabel || 'Values'
      };
    }

    return {
      type: chartConfig.chartType,
      labels: [],
      data: [],
      datasets: [],
      title: chartConfig.title || chartTitle || '',
      xAxisTitle: chartConfig.xAxisLabel || xAxisLabel || 'Categories',
      yAxisTitle: chartConfig.yAxisLabel || yAxisLabel || 'Values'
    };
  };

  // Handle save chart
  const handleSaveChart = () => {
    if (!chartConfig.title.trim()) {
      alert('Please enter a chart title');
      return;
    }



    // Get current user information for audit fields
    const getCurrentUser = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = jwt_decode(token);
          return {
            id: decoded.id,
            username: decoded.username
          };
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
      return { id: null, username: 'Unknown' };
    };

    const currentUser = getCurrentUser();

    // Get the processed chart data first
    const processedChartData = processChartData();
    console.log('chartConfig.chartType:', chartConfig.chartType);
    // Create chart data in the format expected by ChartDisplay component
    const chartData = {
      id: editingChart ? editingChart.id : `chart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Use existing ID if editing, generate unique ID for new charts
      title: chartConfig.title,
      chartType: chartConfig.chartType,
      reportId: reportData?.id,
      createdAt: editingChart ? editingChart.createdAt : new Date().toISOString(), // Keep original creation date if editing
      updatedAt: new Date().toISOString(), // Always update the modification date
      // Map the selected fields based on chart type
      ...(chartConfig.chartType === 'scatter' ? {
        // Scatter chart specific fields
        scatterXAxis: chartConfig.scatterXAxis,
        scatterYAxis: chartConfig.scatterYAxis,
        scatterGroupField: chartConfig.scatterGroupField,
        scatterXAxisLabel: chartConfig.scatterXAxisLabel,
        scatterYAxisLabel: chartConfig.scatterYAxisLabel,
      } : chartConfig.chartType === 'donut' ? {
        // Donut chart specific fields - save donut-specific properties
        pieValueField: chartConfig.pieValueField,
        pieSliceField: chartConfig.pieSliceField,
        showValues: chartConfig.showValues,
        showPercentages: chartConfig.showPercentages,
        showTotal: chartConfig.showTotal,
        legendColors: chartConfig.legendColors,
        valueFieldType: chartConfig.valueFieldType,
        // Also save standard structure for compatibility
        xAxisField: chartConfig.pieSliceField,
        yAxisFields: chartConfig.pieValueField ? [chartConfig.pieValueField] : [],
        xAxisLabel: chartConfig.pieSliceField,
        yAxisLabel: chartConfig.pieValueField,
      } : {
        // Regular chart fields (bar, line, etc.)
        xAxisField: chartConfig.selectedCategoryField,
        yAxisFields: chartConfig.selectedValueFields,
        xAxisLabel: chartConfig.xAxisLabel,
        yAxisLabel: chartConfig.yAxisLabel,
      }),
      legendPosition: chartConfig.legendPosition,
      // Store custom field colors (not applicable for scatter charts and donut charts)
      ...(chartConfig.chartType !== 'scatter' && chartConfig.chartType !== 'donut' ? {
        fieldColors: chartConfig.fieldColors
      } : {}),
      // Store display options
      showTooltips: chartConfig.showTooltips,
      showLabels: chartConfig.showLabels,
      labelColor: chartConfig.labelColor,
      // Store layout options
      sortOrder: chartConfig.sortOrder,
      columnSpan: chartConfig.columnSpan,
      // Store the processed chart data for reference
      chartData: processedChartData,
      // Store the original data for regeneration
      data: {
        labels: processedChartData?.labels || [],
        values: processedChartData?.datasets?.[0]?.data || []
      },
      // Add audit fields based on whether this is a new chart or editing existing
      ...(editingChart ? {
        // For editing: preserve original creation fields, update modification fields
        createdby: editingChart.createdby,
        createddate: editingChart.createddate,
        createdbyid: editingChart.createdbyid,
        lastmodifiedby: currentUser.username,
        lastmodifieddate: new Date().toISOString(),
        lastmodifiedbyid: currentUser.id
      } : {
        // For new chart: set all audit fields to current user
        createdby: currentUser.username,
        createddate: new Date().toISOString(),
        createdbyid: currentUser.id,
        lastmodifiedby: currentUser.username,
        lastmodifieddate: new Date().toISOString(),
        lastmodifiedbyid: currentUser.id
      })
    };

    console.log('Saving chart data:', chartData);

    // Show the charts array that will be sent when saving the report
    console.log('=== CHARTS ARRAY FOR REPORT SAVE ===');
    console.log('Array of charts to be embedded in report data:');
    console.log(JSON.stringify([
      chartData,
      // Example of additional charts in the array:
      // {
      //   "id": "chart_1234567891",
      //   "title": "Employee Age Distribution",
      //   "chartType": "donut",
      //   "xAxisField": "department",
      //   "yAxisFields": ["age"],
      //   "xAxisLabel": "Department",
      //   "yAxisLabel": "Average Age",
      //   "fieldColors": { "age": "#FF6384" },
      //   "showTooltips": true,
      //   "showLabels": true,
      //   "labelColor": "#000000"
      // }
    ], null, 2));
    console.log('=== END CHARTS ARRAY ===');
    onSaveChart(chartData, editingChart ? 'update' : 'create');
    onClose();
  };

  // Render table preview
  const renderTablePreview = () => {
    // Try to use enhanced summarizedData first
    const extractedData = extractDataFromSummarizedData();
    if (extractedData) {
      return (
        <div className="table-preview">
          <div className="table-info">
            <h5>Data from Enhanced SummarizedData</h5>
            <p><strong>Group Field:</strong> {extractedData.groupFieldInfo?.field_label || 'N/A'}</p>
            <p><strong>Summarized Field:</strong> {extractedData.summarizedFieldInfo?.field_label || 'N/A'}</p>
            <p><strong>Aggregation:</strong> {extractedData.summarizedFieldInfo?.aggregation?.toUpperCase() || 'N/A'}</p>
            <p><strong>Data Type:</strong> {extractedData.summarizedFieldInfo?.data_type || 'N/A'}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>{extractedData.xAxisLabel}</th>
                <th>{extractedData.yAxisLabel}</th>
              </tr>
            </thead>
            <tbody>
              {extractedData.labels.map((label, index) => (
                <tr key={index}>
                  <td>{label}</td>
                  <td>{extractedData.values[index]?.toLocaleString() || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Fallback to simple props
    if (!chartLabels || !chartValues || chartLabels.length === 0) {
      return <div className="no-data">No table data available</div>;
    }

    return (
      <div className="table-preview">
        <table>
          <thead>
            <tr>
              <th>{xAxisLabel || 'Category'}</th>
              <th>{yAxisLabel || 'Value'}</th>
            </tr>
          </thead>
          <tbody>
            {chartLabels.map((label, index) => (
              <tr key={index}>
                <td>{label}</td>
                <td>{chartValues[index] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render chart based on type
  const renderChart = () => {
    const chartData = processChartData();
    if (!chartData) return null;

    console.log('Rendering chart with processed data:', chartData);

    // Check if we have valid data
    if (!chartData.labels || chartData.labels.length === 0 || !chartData.datasets || chartData.datasets.length === 0) {
      return (
        <div className="chart-placeholder">
          <div className="placeholder-icon">⚠️</div>
          <p>No data available</p>
          <small>Please select fields and generate preview data</small>
        </div>
      );
    }

    // Chart options for all chart types
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: chartConfig.legendPosition || 'top',
          display: chartConfig.chartType !== 'donut'
        },
        title: {
          display: false // We'll show title separately
        },
        tooltip: {
          enabled: chartConfig.showTooltips !== undefined ? chartConfig.showTooltips : true,
          callbacks: {
            label: function (context) {
              const value = context.parsed.y || context.parsed.x;
              return `${context.dataset.label}: ${value.toLocaleString()}`;
            }
          }
        },
        datalabels: {
          display: chartConfig.showLabels !== undefined ? chartConfig.showLabels : false,
          anchor: 'end',
          align: 'top',
          color: chartConfig.labelColor || '#000000',
          font: {
            weight: 'bold',
            size: 12
          },
          formatter: function (value) {
            return value.toLocaleString();
          }
        }
      },
      scales: chartConfig.chartType !== 'donut' && chartConfig.chartType !== 'table' ? {
        x: {
          display: true,
          title: {
            display: true,
            text: chartConfig.xAxisLabel || xAxisLabel || 'Categories'
          }
        },
        y: {
          display: true,
          title: {
            display: true,
            text: chartConfig.yAxisLabel || yAxisLabel || 'Values'
          },
          beginAtZero: true
        }
      } : {}
    };

    // Render different chart types with multiseries support
    switch (chartConfig.chartType) {
      case 'bar':
        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Bar key={`bar-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={options} />
            </div>
          </div>
        );

      case 'horizontalBar':
        // Enhanced options for horizontal bar charts with consistent bar thickness
        const horizontalBarOptions = {
          ...options,
          indexAxis: 'y',
          elements: {
            bar: {
              borderWidth: 1,
              borderSkipped: false,
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                display: true
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                maxTicksLimit: 20 // Limit number of ticks for better spacing
              }
            }
          },
          plugins: {
            ...options.plugins,
            legend: {
              ...options.plugins.legend,
              position: 'bottom'
            }
          }
        };

        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Bar key={`horizontalBar-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={horizontalBarOptions} />
            </div>
          </div>
        );

      case 'line':
        // Enhanced options for line charts
        const lineOptions = {
          ...options,
          elements: {
            line: {
              tension: 0.4, // Smooth curves
              borderWidth: 3,
              fill: false
            },
            point: {
              radius: 4,
              hoverRadius: 6,
              borderWidth: 2
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            ...options.plugins,
            tooltip: {
              ...options.plugins.tooltip,
              mode: 'index',
              intersect: false
            }
          }
        };

        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Line key={`line-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={lineOptions} />
            </div>
          </div>
        );

      case 'scatter':
        return (
          <ScatterChart
            key={`scatter-${chartConfig.chartType}-${chartConfig.title}`}
            chartConfig={chartConfig}
            chartTitle={chartConfig.title || chartTitle}
            summarizedData={summarizedData}
            chartData={chartData}
          />
        );

      case 'donut':
        console.log('Rendering DonutChart with:', {
          chartConfig: chartConfig,
          summarizedData: summarizedData,
          chartData: chartData
        });
        return (
          <DonutChart
            key={`donut-${chartConfig.chartType}-${chartConfig.title}-${JSON.stringify(chartConfig.legendColors)}-${chartConfig._colorUpdate || 0}`}
            chartConfig={chartConfig}
            chartTitle={chartConfig.title || chartTitle}
            summarizedData={summarizedData}
            chartData={chartData}
            previewData={previewData}
            selectedGroups={selectedGroups}
          />
        );

      case 'area':
        // Create proper area chart data with fill configurations
        const areaChartData = {
          ...chartData,
          datasets: chartData.datasets.map(dataset => ({
            ...dataset,
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            backgroundColor: dataset.backgroundColor + '40', // Add transparency
            borderColor: dataset.borderColor
          }))
        };

        // Enhanced options for area charts
        const areaOptions = {
          ...options,
          elements: {
            line: {
              tension: 0.4, // Smooth curves
              borderWidth: 2
            },
            point: {
              radius: 3,
              hoverRadius: 5,
              borderWidth: 2
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            ...options.plugins,
            tooltip: {
              ...options.plugins.tooltip,
              mode: 'index',
              intersect: false
            }
          },
          scales: {
            ...options.scales,
            x: {
              ...options.scales?.x,
              stacked: false // Allow overlapping areas
            },
            y: {
              ...options.scales?.y,
              stacked: false, // Allow overlapping areas
              beginAtZero: true
            }
          }
        };

        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Line key={`area-${chartConfig.chartType}-${chartConfig.title}`} data={areaChartData} options={areaOptions} />
            </div>
          </div>
        );

      case 'table':
        return (
          <div key={`table-${chartConfig.chartType}-${chartConfig.title}`}>
            {renderTablePreview()}
          </div>
        );

      default:
        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Bar key={`default-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={options} />
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chart-modal-overlay">
      <div className="chart-modal">
        <div className="chart-modal-header">
          <h3>{editingChart ? 'Edit Chart' : 'Add Chart'}</h3>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="chart-modal-content">
          <div className="chart-config-section">
            <div className="form-group">
              <label>Chart Title *</label>
              <input
                type="text"
                value={chartConfig.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter chart title (required for saving)"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Chart Type *</label>
              <div className="chart-type-grid">
                {chartTypes.map(type => (
                  <div
                    key={type.value}
                    className={`chart-type-option ${chartConfig.chartType === type.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange('chartType', type.value)}
                  >
                    <type.icon className="chart-type-icon" />
                    <span className="chart-type-label">{type.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Legend Position - Hide for donut charts as they have their own control */}
            {chartConfig.chartType !== 'donut' && (
              <div className="form-group">
                <label>Legend Position</label>
                <select
                  value={chartConfig.legendPosition || 'top'}
                  onChange={(e) => handleInputChange('legendPosition', e.target.value)}
                  className="form-select"
                >
                  <option value="top">Top</option>
                  <option value="bottom">Bottom</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
                <small className="form-help">Choose where to display the legend on the chart.</small>
              </div>
            )}

            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                min="1"
                value={chartConfig.sortOrder}
                onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 1)}
                placeholder="Enter sort order (1, 2, 3...)"
                className="form-input"
              />
              <small className="form-help">Lower numbers appear first in the report</small>
            </div>

            <div className="form-group">
              <label>Column Span</label>
              <select
                value={chartConfig.columnSpan}
                onChange={(e) => handleInputChange('columnSpan', parseInt(e.target.value))}
                className="form-select"
              >
                <option value={1}>1 Column (Half Width)</option>
                <option value={2}>2 Columns (Full Width)</option>
              </select>
              <small className="form-help">Choose how much horizontal space the chart should take</small>
            </div>

            {/* Scatter Chart Configuration */}
            {chartConfig.chartType === 'scatter' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Group Field (Optional)</label>
                  <select
                    value={chartConfig.scatterGroupField || ''}
                    onChange={(e) => handleInputChange('scatterGroupField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">No grouping (single series)</option>
                    {getAvailableCategoryFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose a field to group data points (e.g., Department). Each group will have different colored points.</small>
                </div>

                <div className="form-group">
                  <label>X-Axis Field *</label>
                  <select
                    value={chartConfig.scatterXAxis || ''}
                    onChange={(e) => handleInputChange('scatterXAxis', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select X-axis field</option>
                    {getAvailableValueFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose which field to display on X-axis (e.g., Age). Values will range from 0 to maximum.</small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Field *</label>
                  <select
                    value={chartConfig.scatterYAxis || ''}
                    onChange={(e) => handleInputChange('scatterYAxis', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select Y-axis field</option>
                    {getAvailableValueFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose which field to display on Y-axis (e.g., Salary). Values will range from 0 to maximum.</small>
                </div>

                <div className="form-group">
                  <label>X-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.scatterXAxisLabel}
                    onChange={(e) => handleInputChange('scatterXAxisLabel', e.target.value)}
                    className="form-input"
                    placeholder="Auto-generated from selected field"
                  />
                  <small className="form-help">Custom label for X-axis. Auto-generated from selected field, but you can customize it.</small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.scatterYAxisLabel}
                    onChange={(e) => handleInputChange('scatterYAxisLabel', e.target.value)}
                    className="form-input"
                    placeholder="Auto-generated from selected field"
                  />
                  <small className="form-help">Custom label for Y-axis. Auto-generated from selected field, but you can customize it.</small>
                </div>
              </>
            )}

            {/* Donut Chart Configuration */}
            {chartConfig.chartType === 'donut' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Value</label>
                  <select
                    value={chartConfig.pieValueField}
                    onChange={(e) => handleInputChange('pieValueField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a value field</option>
                    {getAvailableValueFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Select the value field to use for the donut chart</small>
                </div>

                <div className="form-group">
                  <label>Sliced By</label>
                  <select
                    value={chartConfig.pieSliceField}
                    onChange={(e) => handleInputChange('pieSliceField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a field to slice by</option>
                    {getAvailableCategoryFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Select the field to use for slicing the donut chart</small>
                </div>

                <div className="form-group">
                  <label>Legend Position</label>
                  <select
                    value={chartConfig.legendPosition}
                    onChange={(e) => handleInputChange('legendPosition', e.target.value)}
                    className="form-select"
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </>
            )}

            {/* Field Selection for Category and Value (Non-Scatter and Non-Donut Charts) */}
            {chartConfig.chartType !== 'scatter' && chartConfig.chartType !== 'donut' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Category Field (X-Axis) *</label>
                  <select
                    value={chartConfig.selectedCategoryField}
                    onChange={(e) => handleInputChange('selectedCategoryField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a category field</option>
                    {getAvailableCategoryFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose which field to group data by (X-axis categories)</small>
                </div>

                <div className="form-group">
                  <label>Value Fields (Y-Axis) *</label>
                  <div className="multi-select-container">
                    {getAvailableValueFields().map(field => {
                      const isSelected = chartConfig.selectedValueFields?.includes(field.value);
                      const fieldColor = getFieldColor(field.value, getAvailableValueFields().indexOf(field));
                      return (
                        <div key={field.value} className="multi-select-option">
                          <table className="field-option-table">
                            <tbody>
                              <tr>
                                <td className="checkbox-cell">
                                  <input
                                    type="checkbox"
                                    checked={isSelected || false}
                                    onChange={() => handleValueFieldToggle(field.value)}
                                    className="checkbox-input"
                                    id={`field-${field.value}`}
                                  />
                                </td>
                                <td className="label-cell">
                                  <label htmlFor={`field-${field.value}`} className="field-label">
                                    {field.label}
                                  </label>
                                </td>
                                <td className="color-picker-cell">
                                  {isSelected && (
                                    <input
                                      type="color"
                                      value={fieldColor}
                                      onChange={(e) => handleFieldColorChange(field.value, e.target.value)}
                                      className="color-picker"
                                      title={`Choose color for ${field.label}`}
                                    />
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                  <small className="form-help">Choose one or more aggregated fields to display as values (Y-axis). Multiple fields will create grouped/stacked bars. Click the color picker to customize colors.</small>
                </div>

                {/* Custom Axis Labels */}
                <div className="form-group">
                  <label>X-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.xAxisLabel}
                    onChange={(e) => handleInputChange('xAxisLabel', e.target.value)}
                    className="form-input"
                  />
                  <small className="form-help">Label for X-axis. Auto-generated based on selected field, but you can customize it.</small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.yAxisLabel}
                    onChange={(e) => handleInputChange('yAxisLabel', e.target.value)}
                    className="form-input"
                  />
                  <small className="form-help">Label for Y-axis. Auto-generated based on selected field and aggregation, but you can customize it.</small>
                </div>

              </>
            )}

            {/* Chart Display Options - Available for all chart types */}
            <div className="form-group">
              <label>Chart Display Options</label>
              <div className="display-options">
                <table className="options-table">
                  <tbody>
                    <tr className="option-row">
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={chartConfig.showTooltips}
                          onChange={(e) => handleInputChange('showTooltips', e.target.checked)}
                          className="checkbox-input"
                          id="show-tooltips"
                        />
                      </td>
                      <td className="label-cell">
                        <label htmlFor="show-tooltips" className="option-label">Show tooltips on hover</label>
                      </td>
                      <td className="color-cell"></td>
                    </tr>
                    <tr className="option-row">
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={chartConfig.chartType === 'donut' ? chartConfig.showValues : chartConfig.showLabels}
                          onChange={(e) => handleInputChange(chartConfig.chartType === 'donut' ? 'showValues' : 'showLabels', e.target.checked)}
                          className="checkbox-input"
                          id={chartConfig.chartType === 'donut' ? 'show-values' : 'show-labels'}
                        />
                      </td>
                      <td className="label-cell">
                        <label htmlFor={chartConfig.chartType === 'donut' ? 'show-values' : 'show-labels'} className="option-label">
                          {chartConfig.chartType === 'donut' ? 'Show Values' : 'Show data labels'}
                        </label>
                      </td>
                      <td className="color-cell">
                        {(chartConfig.chartType === 'donut' ? chartConfig.showValues : chartConfig.showLabels) && (
                          <input
                            type="color"
                            value={chartConfig.labelColor}
                            onChange={(e) => handleInputChange('labelColor', e.target.value)}
                            className="color-picker"
                            title={chartConfig.chartType === 'donut' ? 'Choose color for values' : 'Choose color for data labels'}
                          />
                        )}
                      </td>
                    </tr>
                    {/* Show Percentage - Only for Donut Charts */}
                    {chartConfig.chartType === 'donut' && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={chartConfig.showPercentages}
                            onChange={(e) => handleInputChange('showPercentages', e.target.checked)}
                            className="checkbox-input"
                            id="show-percentages"
                          />
                        </td>
                        <td className="label-cell">
                          <label htmlFor="show-percentages" className="option-label">Show Percentage</label>
                        </td>
                        <td className="color-cell">
                          {chartConfig.showPercentages && (
                            <input
                              type="color"
                              value={chartConfig.labelColor}
                              onChange={(e) => handleInputChange('labelColor', e.target.value)}
                              className="color-picker"
                              title="Choose color for percentages"
                            />
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Show Total - Only for Donut Charts */}
                    {chartConfig.chartType === 'donut' && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={chartConfig.showTotal}
                            onChange={(e) => handleInputChange('showTotal', e.target.checked)}
                            className="checkbox-input"
                            id="show-total"
                          />
                        </td>
                        <td className="label-cell">
                          <label htmlFor="show-total" className="option-label">Show Total</label>
                        </td>
                        <td className="color-cell"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <small className="form-help">Control how data is displayed on the chart. Tooltips show values on hover, {chartConfig.chartType === 'donut' ? 'values show directly on donut segments' : 'labels show values directly on chart elements'}.</small>
            </div>

            {/* Legend Colors Section - Only for Donut Charts */}
            {chartConfig.chartType === 'donut' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <div className="form-group">
                <label className="form-label">Legend Colors</label>
                <div className="chart-display-options">
                  <div className="options-container">
                    <table className="options-table legend-colors-table">
                      <tbody>
                        {(() => {
                          // Get unique labels from summarizedData
                          const labels = new Set();
                          const sliceField = chartConfig.pieSliceField;

                          if (sliceField && summarizedData[sliceField]) {
                            Object.keys(summarizedData[sliceField]).forEach(groupValue => {
                              labels.add(groupValue);
                            });
                          }

                          const labelsArray = Array.from(labels);
                          return labelsArray.map((label, index) => {
                            const defaultRgbaColor = getDefaultColorForLabel(index);
                            const defaultHexColor = getDefaultHexColorForLabel(index);
                            const currentColor = chartConfig.legendColors[label] || defaultHexColor;
                            const isCustomColor = chartConfig.legendColors[label];

                            return (
                              <tr key={label} className="option-row">
                                <td className="number-cell">
                                  <span className="legend-number">{index + 1}</span>
                                </td>
                                <td className="label-cell">
                                  <label className="option-label">{label}</label>
                                </td>
                                <td className="color-cell">
                                  <div className="color-controls">
                                    <input
                                      type="color"
                                      value={currentColor}
                                      onChange={(e) => handleLegendColorChange(label, e.target.value)}
                                      className="color-picker"
                                      title={`Choose color for ${label}`}
                                    />
                                    {isCustomColor && (
                                      <button
                                        type="button"
                                        onClick={() => resetLegendColorToDefault(label)}
                                        className="reset-color-btn"
                                        title={`Reset to default color`}
                                      >
                                        ↺
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <small className="form-help">Customize colors for each legend item. Changes will be reflected in the chart immediately.</small>
                </div>
              </div>
            )}
          </div>

          <div className="chart-preview-section">
            <h4>Chart Preview</h4>

            {/* Record Count Information */}


            <div className="chart-preview-container">
              {isLoading ? (
                <div className="chart-loading">
                  <div className="loading-spinner"></div>
                  <p>Generating chart...</p>
                </div>
              ) : (
                <div className="chart-preview">
                  {renderChart()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="chart-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveChart}
            disabled={!chartConfig.title.trim()}
          >
            {editingChart ? 'Update Chart' : 'Add Chart'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportChart;
